import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  format,
  endOfMonth,
  startOfMonth,
  parseISO,
  parse,
  isValid,
} from "date-fns";
import { ClipboardList, RotateCcw } from "lucide-react";
import OurLayouts from "../layout";
import DateRangeFilter from "../shared/DateRangeFilter";
import { APP_ROUTES } from "../../constants/approutes";


const HOSPITAL_API_BASE = process.env.REACT_APP_NEW_DOCTOR_URL || "";



const formatDisplayDate = (value) => {
  if (!value) return "—";
  const parsed = parseISO(String(value));
  return isValid(parsed) ? format(parsed, "dd MMM yyyy") : value;
};

const getDefaultDateRange = () => {
  const now = new Date();
  return {
    fromDate: format(startOfMonth(now), "MM/dd/yyyy"),
    toDate: format(endOfMonth(now), "MM/dd/yyyy"),
  };
};

/** API expects fromDate=2026-05-01&toDate=2026-05-31 */
const toQueryDateParam = (value) => {
  if (!value) return "";
  const slashParsed = parse(value, "MM/dd/yyyy", new Date());
  if (isValid(slashParsed)) return format(slashParsed, "yyyy-MM-dd");
  const isoParsed = parseISO(String(value));
  if (isValid(isoParsed)) return format(isoParsed, "yyyy-MM-dd");
  return value;
};

const getCampStatusStyle = (status = "") => {
  const value = String(status).toUpperCase();
  if (value === "ACTIVE") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (value === "INACTIVE" || value === "CLOSED") {
    return "bg-red-50 text-red-600 border-red-200";
  }
  return "bg-slate-50 text-slate-600 border-slate-200";
};

const CampDetailsList = () => {
  const navigate = useNavigate();
  const hospitalCode = localStorage.getItem("hospitalid") || "";
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [appliedRange, setAppliedRange] = useState(getDefaultDateRange);
  const [campDetails, setCampDetails] = useState([]);
  const [hospitalInfo, setHospitalInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCampDetails = async () => {
      setLoading(true);
      try {
     
          const token = localStorage.getItem("auth_token");
          const code = hospitalCode ;
          const params = new URLSearchParams({
            fromDate: toQueryDateParam(appliedRange.fromDate),
            toDate: toQueryDateParam(appliedRange.toDate),
          });
          const base = HOSPITAL_API_BASE.replace(/\/$/, "");
          const url = `${base}/api/v1/hospital/${encodeURIComponent(code)}/camp-details?${params}`;
          const res = await fetch(url, {
            method: "GET",
            headers: {
              Accept: "application/json",
            
            },
          });
         
          const json = await res.json();
          console.log(json?.hospitalDetails?.campDetails, "cfgvhbjknmlhbk");
          const details = json?.hospitalDetails || json;
          setHospitalInfo(details || null);
          setCampDetails(Array.isArray(details?.campDetails) ? details.campDetails : []);
        
      } catch (error) {
     
        setHospitalInfo();
        setCampDetails( []);
      } finally {
        setLoading(false);
      }
    };

    loadCampDetails();
  }, [appliedRange.fromDate, appliedRange.toDate]);

  const handleDateChange = ({ fromDate, toDate }) => {
    const next = { fromDate, toDate };
    setDateRange(next);
    setAppliedRange(next);
  };

  const handleReset = () => {
    const defaults = getDefaultDateRange();
    setDateRange(defaults);
    setAppliedRange(defaults);
  };

  const handleViewEnquiry = (campCode) => {
    if (!campCode) return;
    navigate(APP_ROUTES.CAMP_DETAILS, { state: { campCode } });
  };

  return (
    <OurLayouts>
      <div className="mt-6 px-2 sm:px-0 max-w-[1400px]">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-100">
            <ClipboardList size={24} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#334155]">Camp Details</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {hospitalInfo?.hospitalName
                ? `${hospitalInfo.hospitalName}`
                : "Hospital camps"}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <DateRangeFilter
              label="Date range"
              fromDate={dateRange.fromDate}
              toDate={dateRange.toDate}
              onChange={handleDateChange}
            />
            <button
              type="button"
              onClick={handleReset}
              className="h-11 px-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <RotateCcw size={15} />
              Reset
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#EDF3F8] border-b border-[#DDE8F2]">
                  {[
                    "Camp Code",
                    "Camp Name",
                    "Start Date",
                    "End Date",
                    "View Enquiry",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3.5 text-left text-[11px] font-bold text-[#0B4D76] uppercase tracking-wide whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-[#004276]">
                      Loading camp details…
                    </td>
                  </tr>
                ) : campDetails.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-slate-500">
                      No camp details found
                    </td>
                  </tr>
                ) : (
                  campDetails.map((camp, index) => (
                    <tr
                      key={camp.campCode || index}
                      className={`border-b border-[#EEEEEE] hover:bg-[#F0F7FF] transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"
                      }`}
                    >
                      <td className="px-4 py-4 text-sm font-medium text-slate-800">
                        {camp.campCode || "—"}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-slate-800">
                        {camp.campName || "—"}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700 whitespace-nowrap">
                        {formatDisplayDate(camp.campStartDate)}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700 whitespace-nowrap">
                        {formatDisplayDate(camp.campEndDate)}
                      </td>
                      {/* <td className="px-4 py-4 text-sm text-slate-700 max-w-xs">
                        {camp.campAddress || "—"}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {camp.campLocation || "—"}
                      </td> */}
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => handleViewEnquiry(camp.campCode)}
                          disabled={!camp.campCode}
                          className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-[#004376] to-[#00BA7E] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          View Enquiry
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </OurLayouts>
  );
};

export default CampDetailsList;
